// Multipart upload helper - used by the new-project / files-page upload
// flows when File.size exceeds MULTIPART_THRESHOLD_BYTES. Below
// that, the simpler single-PUT path in UploadSubmitContext is fine.
//
// Flow per file:
//   1. POST /uploads/initiate_multipart with {size_bytes} -> server
//      starts S3 multipart, returns {upload_id, part_count,
//      part_size_bytes}.
//   2. For each part_number 1..part_count, concurrently (4-way):
//      a. POST /uploads/sign_part -> per-part presigned URL.
//      b. fetch PUT to that URL with body=file.slice(start, end).
//      c. Read the S3-returned ETag from the response 'etag' header.
//   3. POST /uploads/complete_multipart with {upload_id, parts:
//      [{part_number, etag}, ...]}. Server runs CompleteMultipartUpload,
//      HEADs the assembled object, returns the upload record.
//
// The helper exposes a single function the caller awaits. Progress
// is reported per-completed-part via the optional onProgress hook
// so the FE can render a partial progress bar inside the file's
// row in the picked-files preview.
import type { AgentsHttp } from "./useAgentsHttp";
import { withTransientRetry } from "./transientRetry";

// Size at which the FE switches from single-PUT to multipart.
// MUST stay in lock-step with src/inrange_agents/uploads.py:
// MULTIPART_THRESHOLD_BYTES (currently 25 MB).
export const MULTIPART_THRESHOLD_BYTES = 25 * 1024 * 1024;

// 4-way parallelism on part PUTs. Same reasoning as drive.ts's
// runWithConcurrency default: enough to saturate normal home
// uplinks (~25-100 Mbps), low enough that one stuck part doesn't
// starve the rest of the file's progress.
const PART_CONCURRENCY = 4;
// Per-part transient-failure retry uses the shared withTransientRetry helper. A
// large file is many parts, so each part is an independent chance to hit a
// transient blip - a `sign_part` 503 from API Gateway (the integration Lambda
// momentarily un-invokable), an S3 SlowDown/InternalError (503/500), or a
// dropped connection. Without retry a single blip on 1 of N parts aborts the
// whole upload (and the submit). Defaults (4 attempts, 300ms base) suit parts.

interface InitiateMultipartResponse {
  upload_id: string;
  s3_key: string;
  part_size_bytes: number;
  part_count: number;
  expires_at: string;
  max_bytes: number;
  already_attached: boolean;
}

interface SignPartResponse {
  presigned_url: string;
  expires_at: string;
}

interface CompleteMultipartResponse {
  upload_id: string;
  // Present when the completed upload was a ZIP the backend expanded into member
  // documents; the upload flow references these members, not the zip.
  expanded_members?: { upload_id: string; original_name: string }[] | null;
  // The full UploadRecord shape; we only need the above for the upload flow.
  // Wider type kept loose to avoid coupling here.
  [key: string]: unknown;
}

export interface UploadMultipartOpts {
  file: File;
  project_id: string;
  content_type: string;
  source: "local" | "drive";
  drive_file_id?: string;
  // Drive folder path of a Drive-picked file, so an expanded zip's members can
  // record where in Drive the archive lived (member provenance for the agent).
  drive_path?: string;
  // FE-inferred file kind (filename heuristic). Must be sent on
  // /uploads/complete_multipart or the row lands with kind=None and the
  // extractor reports "no extractor for kind (none)". The single-PUT
  // /uploads/complete path already carries this; multipart had the gap.
  kind?: string;
  http: AgentsHttp;
  signal?: AbortSignal;
  onProgress?: (partsCompleted: number, partCount: number) => void;
}

export interface UploadMultipartResult {
  upload_id: string;
  already_attached: boolean;
  // Set when the upload was a ZIP expanded by the backend into member documents.
  expanded_members?: { upload_id: string; original_name: string }[];
}

// Run an async function with at most `concurrency` in flight.
// Returns a Promise that resolves when every item has completed
// (success or failure). Errors are collected, not thrown.
const runParts = async <T>(
  count: number,
  fn: (partNumber: number) => Promise<T>,
  concurrency: number,
  signal?: AbortSignal,
): Promise<{ results: (T | Error)[] }> => {
  const results: (T | Error)[] = new Array(count);
  let nextPart = 1;
  const worker = async (): Promise<void> => {
    while (nextPart <= count) {
      if (signal?.aborted) return;
      const partNumber = nextPart++;
      try {
        results[partNumber - 1] = await fn(partNumber);
      } catch (e) {
        results[partNumber - 1] = e instanceof Error ? e : new Error(String(e));
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, count) }, () => worker()),
  );
  return { results };
};

// Best-effort abort. Failure here is swallowed - the BE's bucket
// lifecycle rule cleans up abandoned parts after 7 days anyway.
const abortQuietly = async (
  http: AgentsHttp,
  upload_id: string,
): Promise<void> => {
  try {
    await http("/uploads/abort_multipart", { upload_id });
  } catch {
    // best-effort
  }
};

export const uploadFileViaMultipart = async (
  opts: UploadMultipartOpts,
): Promise<UploadMultipartResult> => {
  const {
    file,
    project_id,
    content_type,
    source,
    drive_file_id,
    drive_path,
    kind,
    http,
  } =
    opts;

  const init = await http<InitiateMultipartResponse>(
    "/uploads/initiate_multipart",
    {
      project_id,
      original_name: file.name,
      content_type,
      size_bytes: file.size,
      source,
      drive_file_id,
      drive_path,
    },
  );

  // BE dedup branch - file already attached to this project.
  // Skip the multipart pipeline entirely.
  if (init.already_attached) {
    return { upload_id: init.upload_id, already_attached: true };
  }

  const { upload_id, part_size_bytes, part_count } = init;

  // Per-part: sign + PUT + collect ETag. We sign just-in-time
  // rather than batching all signs up front so a stuck per-part
  // upload doesn't burn the 15-min presign window for parts
  // still in the queue.
  const uploadOnePart = async (
    partNumber: number,
  ): Promise<{ part_number: number; etag: string }> => {
    const start = (partNumber - 1) * part_size_bytes;
    const end = Math.min(start + part_size_bytes, file.size);
    const blob = file.slice(start, end);

    // Re-sign + re-PUT as one retryable unit: re-signing on retry is cheap and
    // sidesteps presign expiry, and a transient failure can come from either
    // the sign_part call or the S3 PUT.
    return withTransientRetry(async () => {
      const sign = await http<SignPartResponse>("/uploads/sign_part", {
        upload_id,
        part_number: partNumber,
      });

      const putResp = await fetch(sign.presigned_url, {
        method: "PUT",
        body: blob,
        signal: opts.signal,
      });
      if (!putResp.ok) {
        const detail = await putResp.text();
        const err = new Error(
          `S3 UploadPart ${partNumber}/${part_count} failed: ${putResp.status} ${detail.slice(0, 200)}`,
        ) as Error & { status?: number };
        // Surface the status so the retry classifier can see a transient
        // S3 SlowDown/InternalError (503/500).
        err.status = putResp.status;
        throw err;
      }
      // S3 returns the part's ETag in the response 'etag' header.
      // The header value is wrapped in quotes; strip them so the
      // BE sees a clean hex string.
      const rawEtag =
        putResp.headers.get("etag") ?? putResp.headers.get("ETag");
      if (!rawEtag) {
        throw new Error(
          `S3 UploadPart ${partNumber} returned no ETag header; multipart can't complete without it.`,
        );
      }
      return { part_number: partNumber, etag: rawEtag.replace(/^"|"$/g, "") };
    }, { signal: opts.signal });
  };

  let partsCompleted = 0;
  const { results } = await runParts(
    part_count,
    async (partNumber) => {
      const r = await uploadOnePart(partNumber);
      partsCompleted += 1;
      opts.onProgress?.(partsCompleted, part_count);
      return r;
    },
    PART_CONCURRENCY,
    opts.signal,
  );

  const errors = results.filter((r): r is Error => r instanceof Error);
  if (errors.length > 0) {
    await abortQuietly(http, upload_id);
    throw new Error(
      `Multipart upload of ${file.name} failed on ${errors.length}/${part_count} part(s): ${errors[0].message}`,
    );
  }

  const parts = (results as { part_number: number; etag: string }[]).slice();
  const complete = await http<CompleteMultipartResponse>(
    "/uploads/complete_multipart",
    {
      upload_id,
      parts,
      kind,
    },
  );

  return {
    upload_id: complete.upload_id,
    already_attached: false,
    expanded_members: complete.expanded_members ?? undefined,
  };
};
